import { ArrowRight, Check, Phone } from "lucide-react";

import { QuoteForm } from "@/components/site/QuoteForm";
import { LuxuryVisionBackdrop } from "@/components/site/LuxuryVisionBackdrop";
import { Wordmark } from "@/components/site/Wordmark";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import type { PpcCampaign } from "@/config/campaigns";
import { getProjectBySlug } from "@/config/projects";
import { findServiceArea } from "@/config/service-areas";
import { trackAnalyticsEvent } from "@/lib/analytics";

import type { LocalizedPathBuilder } from "./ServiceAreasIndexPage";

type PpcLandingPageProps = {
  campaign: PpcCampaign;
  buildPath?: LocalizedPathBuilder;
  phoneHref?: string;
  phoneLabel?: string;
  privacyPath?: string;
};

const identityPath: LocalizedPathBuilder = (path) => path;

const processSteps = [
  {
    number: "01",
    title: "Describe the roof",
    body: "Share the property location, roof concern and any visible signs you can document safely.",
  },
  {
    number: "02",
    title: "Assess the condition",
    body: "The roof covering, transitions, penetrations and drainage are reviewed in context.",
  },
  {
    number: "03",
    title: "Define the recommendation",
    body: "Revolin explains the appropriate solution category and what influences the proposed scope.",
  },
  {
    number: "04",
    title: "Confirm the written scope",
    body: "Work proceeds only after the project-specific inclusions, exclusions and next steps are clear.",
  },
] as const;

export function PpcLandingPage({
  campaign,
  buildPath = identityPath,
  phoneHref,
  phoneLabel,
  privacyPath = "/privacy",
}: PpcLandingPageProps) {
  const area = findServiceArea(campaign.location);
  const featuredProject = campaign.featuredProject
    ? getProjectBySlug(campaign.featuredProject)
    : undefined;
  const faqs = campaign.faqs.length > 0 ? campaign.faqs : (area?.faqs ?? []);

  if (!area) {
    throw new Error(`Unknown service area for PPC campaign: ${campaign.location}`);
  }

  return (
    <div id="top" className="min-h-screen bg-background">
      <a
        href="#ppc-main"
        className="sr-only z-[100] rounded-md bg-background px-4 py-3 text-foreground focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
      >
        Skip to content
      </a>
      <header className="border-b border-border bg-background/95 backdrop-blur">
        <div className="container-page flex min-h-20 items-center justify-between gap-4 py-3">
          <Wordmark />
          <div className="flex items-center gap-2">
            {phoneHref && phoneLabel ? (
              <Button variant="ghost" size="sm" asChild className="hidden sm:inline-flex">
                <a
                  href={phoneHref}
                  onClick={() =>
                    trackAnalyticsEvent({ event: "phone_click", link_location: "ppc_header" })
                  }
                >
                  <Phone aria-hidden="true" />
                  {phoneLabel}
                </a>
              </Button>
            ) : null}
            <Button variant="brand" size="pill" asChild>
              <a
                href="#ppc-quote"
                onClick={() =>
                  trackAnalyticsEvent({
                    event: "service_cta_click",
                    cta_location: "ppc_header",
                    service: campaign.service,
                  })
                }
              >
                {campaign.ctaLabel}
              </a>
            </Button>
          </div>
        </div>
      </header>

      <main id="ppc-main" tabIndex={-1}>
        <section className="relative isolate overflow-hidden bg-slate-deep py-16 text-primary-foreground md:py-24">
          <LuxuryVisionBackdrop
            rotationKey={`campaign:${campaign.slug ?? campaign.service}:${campaign.location}`}
            priority
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 -z-10 bg-[linear-gradient(105deg,oklch(0.16_0.06_255/0.97)_0%,oklch(0.16_0.06_255/0.86)_58%,oklch(0.16_0.06_255/0.58)_100%)]"
          />
          <div className="container-page grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(22rem,0.82fr)] lg:items-center lg:gap-16">
            <div>
              <p className="eyebrow">
                {campaign.serviceName} · {area.name}, BC
              </p>
              <h1 className="display-1 mt-5 text-balance">{campaign.headline}</h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-primary-foreground/74 md:text-lg">
                {campaign.valueProposition}
              </p>

              <ul className="mt-8 grid gap-3 text-sm text-primary-foreground/78 sm:grid-cols-2">
                {[
                  "Assessment before recommendation",
                  "Property-specific system review",
                  "Clear written scope",
                  `${area.name} service coverage`,
                ].map((item) => (
                  <li key={item} className="flex gap-2.5">
                    <Check className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>

              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <Button variant="accent" size="xl" asChild>
                  <a
                    href="#ppc-quote"
                    onClick={() =>
                      trackAnalyticsEvent({
                        event: "service_cta_click",
                        cta_location: "ppc_hero",
                        service: campaign.service,
                      })
                    }
                  >
                    {campaign.ctaLabel}
                  </a>
                </Button>
                {phoneHref && phoneLabel ? (
                  <Button variant="outlineDark" size="xl" asChild>
                    <a
                      href={phoneHref}
                      onClick={() =>
                        trackAnalyticsEvent({ event: "phone_click", link_location: "ppc_hero" })
                      }
                    >
                      <Phone aria-hidden="true" />
                      Call {phoneLabel}
                    </a>
                  </Button>
                ) : null}
              </div>
            </div>

            <div id="ppc-quote" className="scroll-mt-6">
              <QuoteForm />
            </div>
          </div>
        </section>

        <section className="section-y">
          <div className="container-page grid gap-12 lg:grid-cols-2 lg:gap-20">
            <div>
              <p className="eyebrow">The problem</p>
              <h2 className="display-2 mt-3 text-balance">A useful scope begins with diagnosis.</h2>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                {campaign.problemStatement}
              </p>
            </div>
            <div className="border-l border-border pl-7 md:pl-10">
              <p className="eyebrow">The approach</p>
              <h2 className="display-3 mt-3">What Revolin evaluates</h2>
              <p className="mt-5 leading-relaxed text-muted-foreground">
                {campaign.solutionSummary}
              </p>
              <a
                href={buildPath(campaign.servicePath)}
                className="mt-7 inline-flex items-center gap-2 text-sm font-medium underline-offset-4 hover:underline"
              >
                Learn about {campaign.serviceName.toLowerCase()}
                <ArrowRight className="size-4" aria-hidden="true" />
              </a>
            </div>
          </div>
        </section>

        <section className="section-y border-y border-border bg-muted/50">
          <div className="container-page">
            <div className="max-w-2xl">
              <p className="eyebrow">A transparent process</p>
              <h2 className="display-2 mt-3 text-balance">From roof concern to clear next step</h2>
            </div>
            <ol className="mt-12 grid gap-px overflow-hidden rounded-lg border border-border bg-border md:grid-cols-2 lg:grid-cols-4">
              {processSteps.map((step) => (
                <li key={step.number} className="bg-card p-6">
                  <span className="font-display text-xl text-accent">{step.number}</span>
                  <h3 className="mt-8 text-lg font-medium text-foreground">{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="section-y">
          <div className="container-page grid gap-12 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:gap-20">
            <div>
              <p className="eyebrow">Service-area relevance</p>
              <h2 className="display-2 mt-3 text-balance">
                Planned for {area.name}, not a swapped city name
              </h2>
              <p className="mt-5 leading-relaxed text-muted-foreground">{area.localContext}</p>
              <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
                Revolin Roofing serves {area.name}; this page does not claim a physical office in
                the city.
              </p>
              <a
                href={buildPath(`/service-areas/${area.slug}`)}
                className="mt-7 inline-flex items-center gap-2 text-sm font-medium underline-offset-4 hover:underline"
              >
                Read the {area.name} service-area guide
                <ArrowRight className="size-4" aria-hidden="true" />
              </a>
            </div>

            <div>
              <p className="eyebrow">Assessment focus</p>
              <ul className="mt-5 divide-y divide-border border-y border-border">
                {area.assessmentFocus.map((item) => (
                  <li key={item} className="flex gap-3 py-5 text-sm leading-relaxed">
                    <Check className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="section-y border-y border-border bg-muted/50">
          <div className="container-page grid gap-12 lg:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)]">
            <div>
              <p className="eyebrow">Before & after</p>
              <h2 className="display-2 mt-3 text-balance">
                See roofing transformations across Metro Vancouver.
              </h2>
              {featuredProject ? (
                <div className="mt-5 max-w-md">
                  <p className="text-base font-medium text-foreground">{featuredProject.title}</p>
                  <p className="mt-3 leading-relaxed text-muted-foreground">
                    {featuredProject.shortProblem ??
                      featuredProject.solution ??
                      "Browse before-and-after galleries for leak repair, asphalt, metal, cedar, and flat-roof work."}
                  </p>
                  <a
                    href={buildPath(`/before-after/${featuredProject.slug}`)}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-medium underline-offset-4 hover:underline"
                  >
                    View project gallery
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </a>
                </div>
              ) : (
                <p className="mt-5 max-w-md leading-relaxed text-muted-foreground">
                  Browse Revolin Roofing before-and-after galleries for Metro Vancouver homeowners -
                  leak repair through full asphalt, metal, cedar, and flat-roof replacements.
                </p>
              )}
            </div>

            <div>
              <p className="eyebrow">Common questions</p>
              <Accordion type="single" collapsible className="mt-4 w-full">
                {faqs.map((item, index) => (
                  <AccordionItem key={item.question} value={`ppc-faq-${index}`}>
                    <AccordionTrigger className="text-left text-[0.98rem] font-medium">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        <section className="section-y bg-slate-deep text-primary-foreground">
          <div className="container-page flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <p className="eyebrow">Next step</p>
              <h2 className="display-2 mt-3 text-balance">
                Make the decision from documented roof condition.
              </h2>
              <p className="mt-4 leading-relaxed text-primary-foreground/68">
                Request an assessment for the {area.name} property and describe the concern you want
                reviewed.
              </p>
            </div>
            <Button variant="accent" size="xl" asChild>
              <a
                href="#ppc-quote"
                onClick={() =>
                  trackAnalyticsEvent({
                    event: "service_cta_click",
                    cta_location: "ppc_footer",
                    service: campaign.service,
                  })
                }
              >
                {campaign.ctaLabel}
              </a>
            </Button>
          </div>
        </section>
      </main>

      <footer className="border-t border-border bg-card">
        <div className="container-page flex flex-col gap-4 py-6 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>Revolin Roofing · A Revolin Group Company</p>
          <a href={buildPath(privacyPath)} className="underline-offset-4 hover:underline">
            Privacy
          </a>
        </div>
      </footer>
    </div>
  );
}
